<!--
    REV-21370 — `Neue Korrekturbuchung` / `Korrekturbuchung bearbeiten` (Pure View), ADR-0015 §6.

    ⚠️ The OVERLAY is the HOST's job, not this component's — the same division `abcp-upload-modal`
    already uses (REV-21363). The panel positions itself `static`, so the Component Lab can render two
    of them side by side, and the production host puts one over the page. A `position: fixed` here
    would make the lab unusable and would take the placement decision away from the surface that knows
    where the dialog belongs. The hosts frame it with `ham-modal` (no header, no footer: both live
    here, with their test markers); header and footer copy `ham-modal`'s look.

    No `<p>` in here: `#global-modal-placeholder p` centres and greys every paragraph in a modal.
-->
<div
    class="adjm"
    data-testid="adjustment-modal"
    role="dialog"
    aria-modal="true"
    [attr.data-mode]="mode"
    [attr.aria-label]="titleKey | i18next"
    #panel
>
    <header class="adjm-head">
        <div>
            <h2 class="adjm-head__title" data-testid="adjustment-modal-title">{{ titleKey | i18next }}</h2>
            <!-- `Konto <nr> · <bezeichnung>` — the edit dialog's sub-header, as the prototype has it. -->
            <div class="adjm-head__sub" data-testid="adjustment-modal-subtitle" *ngIf="isEdit && subtitle">
                {{ 'abcp_adjustments_modal_account' | i18next }} {{ subtitle }}
            </div>
        </div>
        <ham-button
            class="adjm-head__close"
            data-testid="adjustment-modal-close"
            variant="transparent"
            size="xs"
            [hideText]="true"
            icon="assets/hamIcons/x.svg"
            (buttonClick)="onCancel()"
        ></ham-button>
    </header>

    <div class="adjm-body">
        <!--
            No account plan — the dialog says so instead of offering two empty pickers. That is a real
            state: a closing that has never been synced with its ERP has no chart of accounts yet.
        -->
        <div class="adjm-note adjm-note--empty" data-testid="adjustment-modal-no-accounts" *ngIf="accounts.length === 0">
            {{ 'abcp_adjustments_modal_no_accounts' | i18next }}
        </div>

        <ng-container *ngIf="accounts.length > 0">
            <div class="adjm-field">
                <textarea
                    hamInput
                    data-testid="adjustment-field-text"
                    rows="2"
                    [label]="'abcp_adjustments_field_text' | i18next"
                    [hideRemoveXButton]="true"
                    [(ngModel)]="text"
                    [attr.placeholder]="'abcp_adjustments_field_text_placeholder' | i18next"
                ></textarea>
            </div>

            <!-- Searchable by number and name; the clear button empties the pick. -->
            <div class="adjm-row">
                <div class="adjm-field">
                    <input
                        hamSimpleDropDown
                        [restoreSelectionOnBlur]="true"
                        data-testid="adjustment-field-debit"
                        [label]="'abcp_adjustments_field_debit' | i18next"
                        [placeholder]="'abcp_adjustments_field_choose' | i18next"
                        [options]="accountOptions"
                        [optionKeys]="['label']"
                        returnKey="id"
                        [ngModel]="debitAccountId"
                        (ngModelChange)="debitAccountId = $event ?? ''"
                    />
                </div>

                <div class="adjm-field">
                    <input
                        hamSimpleDropDown
                        [restoreSelectionOnBlur]="true"
                        data-testid="adjustment-field-credit"
                        [label]="'abcp_adjustments_field_credit' | i18next"
                        [placeholder]="'abcp_adjustments_field_choose' | i18next"
                        [options]="accountOptions"
                        [optionKeys]="['label']"
                        returnKey="id"
                        [ngModel]="creditAccountId"
                        (ngModelChange)="creditAccountId = $event ?? ''"
                    />
                </div>
            </div>

            <!--
                Soll ≠ Haben (AC5). The user is TOLD rather than merely blocked: a button that greys out
                with no explanation is the same as a button that is broken.
            -->
            <div class="adjm-error" data-testid="adjustment-modal-same-accounts" *ngIf="hasSameAccounts">
                {{ 'abcp_adjustments_error_same_accounts' | i18next }}
            </div>

            <div class="adjm-row">
                <!--
                    ⚠️ **NO `Buchungsdatum` here** (prototype, 03.09.2026). A new correction is
                    posted as of the balance sheet date — where a closing entry belongs, and which
                    the server applies. Offering the field here would put one pre-filled input the
                    user almost never changes in the place where they are busiest.
                    Moving the date inside the fiscal year IS supported: it happens on the DETAIL
                    surface, where the correction already exists and changing its date is a
                    considered edit rather than part of writing it.
                -->

                <div class="adjm-field">
                    <!-- `hamInput` number field: shows the formatted amount, edits on click. -->
                    <input
                        hamInput
                        type="number"
                        step="0.01"
                        min="0"
                        data-testid="adjustment-field-amount"
                        placeholder="0.00"
                        [label]="('abcp_adjustments_field_amount' | i18next) + ' (' + currency + ')'"
                        [hideRemoveXButton]="true"
                        [(ngModel)]="amount"
                    />
                </div>

                <!--
                    `Zuordnung (Konto)` — the CREATE dialog only (see the component doc). Blank means
                    `— automatisch (Soll) —`, i.e. attribute the correction to the debit account.
                -->
                <div class="adjm-field" *ngIf="showOwnerField">
                    <input
                        hamSimpleDropDown
                        [restoreSelectionOnBlur]="true"
                        data-testid="adjustment-field-owner"
                        [label]="'abcp_adjustments_field_owner' | i18next"
                        [placeholder]="'abcp_adjustments_field_owner_auto' | i18next"
                        [options]="accountOptions"
                        [optionKeys]="['label']"
                        returnKey="id"
                        [ngModel]="ownerAccountId"
                        (ngModelChange)="ownerAccountId = $event ?? ''"
                    />
                </div>
            </div>

            <div class="adjm-field">
                <textarea
                    hamInput
                    data-testid="adjustment-field-remark"
                    rows="2"
                    [label]="'abcp_adjustments_field_remark' | i18next"
                    [hideRemoveXButton]="true"
                    [(ngModel)]="remark"
                    [attr.placeholder]="'abcp_adjustments_field_remark_placeholder' | i18next"
                ></textarea>
            </div>

            <!-- The live preview: `Buchungssatz: <soll> an <haben> · CHF <betrag>` (AC5). -->
            <div class="adjm-preview" data-testid="adjustment-modal-preview">
                {{ 'abcp_adjustments_preview' | i18next }}:
                <strong>{{ previewDebit || '—' }}</strong>
                {{ 'abcp_adjustments_preview_against' | i18next }}
                <strong>{{ previewCredit || '—' }}</strong>
                · {{ currency }}&nbsp;<ng-container *ngIf="previewAmountMinor !== null">{{
                    previewAmountMinor / 100 | finNumber: '1.2-2'
                }}</ng-container
                ><ng-container *ngIf="previewAmountMinor === null">—</ng-container>
            </div>
        </ng-container>

        <!-- A refusal from the backend, as a translation key — never foreign prose (AC13). -->
        <div class="adjm-error" data-testid="adjustment-modal-error" *ngIf="errorKey">{{ errorKey | i18next }}</div>
    </div>

    <footer class="adjm-foot">
        <ham-button data-testid="adjustment-modal-cancel" variant="white" (buttonClick)="onCancel()">
            {{ 'abcp_adjustments_modal_cancel' | i18next }}
        </ham-button>
        <!--
            The primary button stays INACTIVE until text, both accounts and an amount greater than zero
            are there (AC5). Comfort, not a boundary: the backend enforces the same rules.
        -->
        <ham-button
            data-testid="adjustment-modal-submit"
            variant="primary"
            [disabled]="!canSubmit"
            (buttonClick)="onSubmit()"
        >
            {{ submitLabelKey | i18next }}
        </ham-button>
    </footer>
</div>
